import {ACTION_CONTROL_HEIGHT_PX} from "theme/tokens";
import {CAMPAIGN_SPACE_GAP_PX} from "./campaignSpace";

export const RECIPIENT_HEADER_GAP_PX = 24;
export const RECIPIENT_BOTTOM_GAP_PX = 16;
// The sticky file-count header is indivisible even when only a fraction of a file row fits.
export const RECIPIENT_FILE_LIST_HEADER_HEIGHT_PX = 44;
export const RECIPIENT_CAMPAIGN_SLOT_HEIGHT_PX = ACTION_CONTROL_HEIGHT_PX + 2 * CAMPAIGN_SPACE_GAP_PX;
/** One file row, measured on the real list at 375px and at 1440px, where both come to 69px. */
export const RECIPIENT_FILE_ROW_HEIGHT_PX = 69;
/**
 * The height the reading list is never reduced below: its header strip plus ONE whole row.
 *
 * ONE ROW (owner 2026-09-15, later: "the recipient/upload page should be fixed not scroll with the
 * page"). It was briefly three, which was the room a list could keep while the PAGE was allowed to
 * scroll for whatever would not fit. The page is not allowed to any more, so this floor is no longer a
 * comfort setting: it is the last thing that can give way before the card stops fitting the window, and
 * three rows is 138px of card that a short phone with a consent bar up does not have. One whole row
 * still says what the list holds, which was the objection to the 136px row-and-a-fraction it replaced.
 *
 * It is one number for two readers, deliberately: the list's CSS floor
 * (`download/recipient/primitives.tsx`) and the arithmetic below that decides how much room the card
 * needs. Two spellings of it would drift, and the drift would show up as a card whose content does not
 * fit the height its own page reserved for it.
 */
export const RECIPIENT_FITTING_LIST_HEIGHT_PX =
	RECIPIENT_FILE_LIST_HEADER_HEIGHT_PX + RECIPIENT_FILE_ROW_HEIGHT_PX;
/**
 * No recipient card fits in less than this, whatever a mid-render measurement claims.
 *
 * Measured: the smallest fixed parts any ready card draws are a peer transfer's 311px, and with the
 * list floor above that comes to 562px. A measurement below it is a card that has not finished
 * rendering, and treating one as the truth would flash a campaign in beside a card that cannot host it.
 */
export const RECIPIENT_MIN_FITTING_CARD_HEIGHT_PX = 560;

export interface CampaignFitInput {
	sideBySide: boolean;
	viewportHeight: number;
	fittingCardHeight: number;
	headerClearance: number;
	cookieReserve: number;
	minimumCampaignSlotHeight: number;
}

/** A side column costs the card no height; only the mobile strip needs spare vertical space. */
export function campaignFit({sideBySide, ...measurements}: CampaignFitInput): boolean {
	if (sideBySide) return true;
	const {viewportHeight, fittingCardHeight, headerClearance, cookieReserve, minimumCampaignSlotHeight} = measurements;
	return Object.values(measurements).every(value => Number.isFinite(value) && value >= 0)
		&& viewportHeight > 0 && fittingCardHeight > 0
		&& viewportHeight >= fittingCardHeight + headerClearance + cookieReserve + minimumCampaignSlotHeight;
}

export const RECIPIENT_GIFT_GAP_PX = 14;

/**
 * A gift hanging under the card may use spare room, never the reading list or the cookie reserve.
 *
 * A `docked` gift is the tab a phone shows on the card's edge, which opens the gift in a dialog (owner
 * 2026-10-07, ui#3372). It sits beside the card, out of the flow, so it needs none of the room under the
 * card and shows whenever the card does.
 */
export function recipientGiftFits({docked, ...input}: {
	viewportHeight: number; contentHeight: number; listHeight: number; listContentHeight: number;
	tagHeight: number; headerClearance: number; cookieReserve: number; docked: boolean;
}): boolean {
	return input.contentHeight > 0 && input.tagHeight > 0
		&& Object.values(input).every(value => Number.isFinite(value) && value >= 0)
		&& (docked || campaignFit({
			sideBySide: false,
			viewportHeight: input.viewportHeight,
			fittingCardHeight:
				recipientCardFloorHeight(input.contentHeight, input.listHeight, input.listContentHeight),
			headerClearance: input.headerClearance,
			cookieReserve: input.cookieReserve,
			minimumCampaignSlotHeight: input.tagHeight + RECIPIENT_GIFT_GAP_PX,
		}));
}

/**
 * The height the card gives up for its gift: what the gift occupies under it plus the gap above the gift,
 * and nothing when the gift does not fit or is a tab docked beside the card (see {@link recipientGiftFits}).
 */
export function recipientGiftReserve({fits, docked, occupiedHeight}: {fits: boolean; docked: boolean; occupiedHeight: number}): number {
	return fits && !docked ? occupiedHeight + RECIPIENT_GIFT_GAP_PX : 0;
}

/**
 * The height below which the card stops shrinking with its column (ui#2457): its fixed parts, plus as
 * much reading list as the rule keeps. Replace only the list's variable height; shrinking the campaign
 * column cannot change this answer.
 *
 * The list's share is its FLOOR or the whole of it, whichever is smaller, and the smaller case is not a
 * detail: a one-file transfer's list is shorter than the floor, and reserving the floor for it would
 * leave the card taller than its own content with an empty strip under the single row.
 *
 * TWO CALLERS, one answer. The card publishes this as its own `min-height`, and the campaign asks it
 * whether there is room left under the card for a strip. They have to agree: a campaign placed against a
 * smaller number would take room the card then reclaims by pushing the download button off the window.
 */
export function recipientCardFloorHeight(
	contentHeight: number, listHeight: number, listContentHeight: number,
): number {
	return Math.ceil(
		contentHeight - listHeight + Math.min(RECIPIENT_FITTING_LIST_HEIGHT_PX, listContentHeight));
}

/**
 * The same floor for the CAMPAIGN's decision, never below {@link RECIPIENT_MIN_FITTING_CARD_HEIGHT_PX}.
 *
 * The card itself must not carry that lower bound — it would force an empty strip under a short dead
 * card — but the campaign must: it is deciding whether to mount a whole strip, and a measurement taken
 * mid-render is the one input that would tell it yes when the finished card leaves no room.
 */
export function fittingRecipientCardHeight(
	contentHeight: number, listHeight: number, listContentHeight: number,
): number {
	return Math.max(RECIPIENT_MIN_FITTING_CARD_HEIGHT_PX,
		recipientCardFloorHeight(contentHeight, listHeight, listContentHeight));
}
